export const PLAYER_TOKEN_STORAGE_KEY = 'arabquiz_player_token'

/**
 * Solo sessions are stored under a SEPARATE key from the multiplayer player
 * session so the two reconnect paths never collide: a host playing solo and a
 * player on the same browser keep independent stored sessions, and clearing one
 * never touches the other. The host route reads this on mount to decide between
 * `ReconnectSolo` (resume solo) and `JoinAsHost` (normal lobby).
 */
export const SOLO_SESSION_STORAGE_KEY = 'arabquiz.soloSession'

export interface StoredPlayerSession {
  gameId: string
  token: string
  nickname: string
}

/**
 * Player sessions live in `localStorage`, scoped per origin.
 *
 * A player session identifies ONE player in ONE game (gameId + token +
 * nickname). We use `localStorage` (shared across every tab of the same
 * origin) deliberately: when a player opens the game in a SECOND tab of the
 * same browser, they should rejoin as the SAME player — the second tab reads
 * the stored session and `Reconnect`s to the existing player rather than
 * spawning a duplicate. The hub enforces "newest connection wins" on
 * reconnect, so the freshest tab is the live one. Surviving an in-tab refresh
 * is the same mechanism. (Two genuinely different players on one shared device
 * is not a supported single-browser scenario — they should use separate
 * devices or a private/incognito window, which has its own storage.)
 */
function getStore(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export function decodeGameIdFromPlayerToken(token: string): string | null {
  try {
    const decoded = globalThis.atob(token)
    const [gameId] = decoded.split('|')
    return gameId || null
  } catch {
    return null
  }
}

export function readStoredPlayerSession(): StoredPlayerSession | null {
  const store = getStore()
  if (!store) return null

  try {
    const raw = store.getItem(PLAYER_TOKEN_STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<StoredPlayerSession>
    if (
      typeof parsed.gameId !== 'string' ||
      typeof parsed.token !== 'string' ||
      typeof parsed.nickname !== 'string'
    ) {
      return null
    }

    return parsed as StoredPlayerSession
  } catch {
    return null
  }
}

export function writeStoredPlayerSession(session: StoredPlayerSession) {
  const store = getStore()
  if (!store) return

  try {
    store.setItem(PLAYER_TOKEN_STORAGE_KEY, JSON.stringify(session))
  } catch {
    // Best-effort only; a storage failure should not block gameplay.
  }
}

export function clearStoredPlayerSession() {
  const store = getStore()
  if (!store) return

  try {
    store.removeItem(PLAYER_TOKEN_STORAGE_KEY)
  } catch {
    // Best-effort only; a storage failure should not block gameplay.
  }
}

export function readStoredSoloSession(): StoredPlayerSession | null {
  const store = getStore()
  if (!store) return null

  try {
    const raw = store.getItem(SOLO_SESSION_STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<StoredPlayerSession>
    if (
      typeof parsed.gameId !== 'string' ||
      typeof parsed.token !== 'string' ||
      typeof parsed.nickname !== 'string'
    ) {
      return null
    }

    return parsed as StoredPlayerSession
  } catch {
    return null
  }
}

export function writeStoredSoloSession(session: StoredPlayerSession) {
  const store = getStore()
  if (!store) return

  try {
    store.setItem(SOLO_SESSION_STORAGE_KEY, JSON.stringify(session))
  } catch {
    // Best-effort only; a storage failure should not block gameplay.
  }
}

export function clearStoredSoloSession() {
  const store = getStore()
  if (!store) return

  try {
    store.removeItem(SOLO_SESSION_STORAGE_KEY)
  } catch {
    // Best-effort only; a storage failure should not block gameplay.
  }
}
